<script setup lang="ts">
defineProps({
  article: {
    type: Object as PropType<Sanity.Default.Schema.Articles>,
    required: true,
  },
})

const { urlFor } = useSanityImage()
const { extractAndTruncate } = useTruncate()
</script>

<template>
  <div
    v-if="article"
    class="relative group h-full overflow-hidden flex flex-col justify-between"
  >
    <nuxt-link
      :to="`/articles/${article.slug.current}`"
      class="inset-0 absolute z-10"
    >
      <span class="sr-only">Read Article</span>
    </nuxt-link>
    <div class="header overflow-hidden">
      <nuxt-img
        v-if="article.image"
        :src="urlFor(article.image).width(608).height(342).fit('crop').url()"
        quality="85"
        :alt="article.image.alt"
        :width="608"
        :height="342"
        class="object-cover object-center w-full aspect-[16/9] transition-all transform group-hover:scale-[101%] transform-origin-center duration-300 ease-in-out group-hover:opacity-90"
        loading="lazy"
      />
      <div class="body xl:max-w-xl mt-4">
        <div class="footer pb-3 mb-3 border-b border-gray-200">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center justify-start gap-3">
              <nuxt-img
                v-if="article.author?.photo"
                :src="urlFor(article.author.photo).width(40).height(40).url()"
                quality="100"
                :alt="article.author.name"
                width="40"
                height="40"
                class="rounded-full object-cover object-center shrink-0"
                loading="lazy"
              />
              <div>
                <div v-if="article.author?.name" class="text-sm">
                  By {{ article.author.name }}
                </div>
                <div v-if="article.date" class="text-gray-700 text-xs">
                  {{ new Date(article.date).toLocaleDateString('en-UK', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <nuxt-link
          :to="`/articles/${article.slug.current}`"
          class="hover:opacity-80"
        >
          <h3 class="tracking-tighter text-lg lg:text-lg mb-2.5 group-hover:opacity-80">
            {{ article.heading }}
          </h3>
        </nuxt-link>
        <p v-if="article.subtitle" class="text-sm line-clamp-3">
          {{ article.subtitle }}
        </p>
        <p v-else-if="article.body" class="text-sm">
          {{ extractAndTruncate(article.body, 300) }}
        </p>
        <UiBtn
          :to="`/articles/${article.slug.current}`"
          variant="link"
          label=""
          :padded="false"
          size="xl"
          class="my-2.5"
        >
          <span class="sr-only">{{ article.title }}</span>
        </UiBtn>
      </div>
    </div>
  </div>
</template>
